import type { Habit, HabitCreate, HabitLog, HabitUpdate, Task, TaskCreate, TaskUpdate } from '@obv/api-client';
import { ApiError } from '@obv/api-client';
import { addDays, compareTasks } from '@obv/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryClient } from './client';
import { keys } from './keys';

/** Completed tasks stay visible (for undo and "done today") for a week. */
export const DONE_WINDOW_DAYS = 7;
export const HABIT_LOG_DAYS = 42;

export interface TaskList {
  items: Task[];
  since: string;
}

async function fetchTasks(doneSince: string, serverTime: string): Promise<TaskList> {
  const items: Task[] = [];
  let cursor: string | undefined;
  do {
    const page = await api.tasks.list({ done_since: doneSince, cursor, limit: 200 });
    items.push(...page.items);
    cursor = page.next_cursor || undefined;
  } while (cursor);
  let since = serverTime;
  for (const t of items) if (t.updated_at > since) since = t.updated_at;
  return { items: items.sort(compareTasks), since };
}

function mergeTasks(current: Task[], changes: Task[], doneSince: string, completedOn: (t: Task) => string | null): Task[] {
  const byId: Record<string, Task> = {};
  for (const t of current) byId[t.id] = t;
  for (const c of changes) {
    if (c.deleted_at) delete byId[c.id];
    else byId[c.id] = c;
  }
  return Object.keys(byId)
    .map((k) => byId[k]!)
    .filter((t) => !t.completed || (completedOn(t) || '') >= doneSince)
    .sort(compareTasks);
}

/** Every open task plus the last week's completed ones, kept fresh by the delta feed. */
export function useTasks(today: string, serverTime: string | undefined, completedOn: (t: Task) => string | null) {
  const doneSince = addDays(today, -DONE_WINDOW_DAYS);
  const key = keys.tasks.list(doneSince);
  const qc = useQueryClient();
  const query = useQuery({ queryKey: key, queryFn: () => fetchTasks(doneSince, serverTime!), enabled: !!serverTime, staleTime: 10 * 60_000 });
  useQuery({
    queryKey: [...key, 'changes'],
    enabled: query.isSuccess,
    refetchInterval: 60_000,
    queryFn: async () => {
      const cur = qc.getQueryData<TaskList>(key);
      if (!cur) return 0;
      const res = await api.tasks.changes(cur.since);
      qc.setQueryData<TaskList>(key, (old) =>
        old ? { items: res.items.length ? mergeTasks(old.items, res.items, doneSince, completedOn) : old.items, since: res.next_since } : old,
      );
      return res.items.length;
    },
  });
  return query;
}

type Snap = ReturnType<typeof snapshotTasks>;
const snapshotTasks = () => queryClient.getQueriesData<TaskList>({ queryKey: keys.tasks.all() });
const restoreTasks = (s: Snap | undefined) => s && s.forEach(([k, v]) => queryClient.setQueryData(k, v));
function patchTasks(fn: (items: Task[]) => Task[]) {
  queryClient.setQueriesData<TaskList>({ queryKey: keys.tasks.all() }, (old) =>
    old && Array.isArray(old.items) ? { ...old, items: fn(old.items).sort(compareTasks) } : old,
  );
}

type CreateTaskVars = { body: TaskCreate; key: string; tempId: string };
type UpdateTaskVars = { id: string; body: TaskUpdate; key: string };
type DeleteTaskVars = { id: string; key: string };

queryClient.setMutationDefaults(['tasks', 'create'], { mutationFn: (v: CreateTaskVars) => api.tasks.create(v.body, v.key) });
queryClient.setMutationDefaults(['tasks', 'update'], { mutationFn: (v: UpdateTaskVars) => api.tasks.update(v.id, v.body, v.key) });
queryClient.setMutationDefaults(['tasks', 'delete'], { mutationFn: (v: DeleteTaskVars) => api.tasks.remove(v.id, v.key) });

/** Recurring completions spawn the next occurrence server-side; refetch to pick it up. */
const refreshTasks = () => queryClient.invalidateQueries({ queryKey: keys.tasks.all() });

export function useCreateTask() {
  return useMutation<Task, ApiError, CreateTaskVars, { snap: Snap }>({
    mutationKey: ['tasks', 'create'],
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: keys.tasks.all() });
      const snap = snapshotTasks();
      const temp: Task = {
        id: v.tempId,
        title: v.body.title,
        notes: v.body.notes ?? null,
        member_id: v.body.member_id ?? null,
        due_date: v.body.due_date ?? null,
        priority: v.body.priority || 'medium',
        recurrence: v.body.recurrence || 'none',
        series_id: null,
        completed: false,
        completed_at: null,
        updated_at: new Date(0).toISOString(),
        deleted_at: null,
      };
      patchTasks((items) => [...items, temp]);
      return { snap };
    },
    onSuccess: (task, v) => patchTasks((items) => items.map((t) => (t.id === v.tempId ? task : t))),
    onError: (_e, _v, ctx) => restoreTasks(ctx && ctx.snap),
  });
}

export function useUpdateTask() {
  return useMutation<Task, ApiError, UpdateTaskVars, { snap: Snap }>({
    mutationKey: ['tasks', 'update'],
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: keys.tasks.all() });
      const snap = snapshotTasks();
      patchTasks((items) =>
        items.map((t) => {
          if (t.id !== v.id) return t;
          const next = { ...t, ...v.body } as Task;
          if (v.body.completed !== undefined) next.completed_at = v.body.completed ? new Date().toISOString() : null;
          return next;
        }),
      );
      return { snap };
    },
    onSuccess: (task, v) => {
      patchTasks((items) => items.map((t) => (t.id === task.id ? task : t)));
      if (v.body.completed && task.recurrence !== 'none') refreshTasks();
    },
    onError: (_e, _v, ctx) => restoreTasks(ctx && ctx.snap),
  });
}

export function useDeleteTask() {
  return useMutation<void, ApiError, DeleteTaskVars, { snap: Snap }>({
    mutationKey: ['tasks', 'delete'],
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: keys.tasks.all() });
      const snap = snapshotTasks();
      patchTasks((items) => items.filter((t) => t.id !== v.id));
      return { snap };
    },
    onError: (e, _v, ctx) => {
      if (e.code !== 'task.not_found') restoreTasks(ctx && ctx.snap);
    },
  });
}

export function useRollover() {
  return useMutation<{ moved: number }, ApiError, { to: string; key: string }, { snap: Snap }>({
    mutationFn: (v) => api.tasks.rollover(v.to, v.key),
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: keys.tasks.all() });
      const snap = snapshotTasks();
      patchTasks((items) => items.map((t) => (!t.completed && t.due_date !== null && t.due_date < v.to ? { ...t, due_date: v.to } : t)));
      return { snap };
    },
    onSuccess: refreshTasks,
    onError: (_e, _v, ctx) => restoreTasks(ctx && ctx.snap),
  });
}

// ---- Habits ----

export function useHabits(enabled = true) {
  return useQuery({ queryKey: keys.habits.list(), queryFn: () => api.habits.list().then((r) => r.items), enabled, refetchInterval: 5 * 60_000 });
}

export function habitLogRange(today: string) {
  return { from: addDays(today, -HABIT_LOG_DAYS), to: today };
}

export function useHabitLogs(today: string, enabled = true) {
  const { from, to } = habitLogRange(today);
  return useQuery({
    queryKey: keys.habits.logs(from, to),
    queryFn: () => api.habits.logs(from, to).then((r) => r.items),
    enabled,
    refetchInterval: 60_000,
  });
}

type LogVars = { habitId: string; date: string; done: boolean };

queryClient.setMutationDefaults(['habits', 'log'], { mutationFn: (v: LogVars) => api.habits.setLog(v.habitId, v.date, v.done) });

export function useSetHabitLog() {
  return useMutation<HabitLog, ApiError, LogVars, { snap: [readonly unknown[], HabitLog[] | undefined][] }>({
    mutationKey: ['habits', 'log'],
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: keys.habits.allLogs() });
      const snap = queryClient.getQueriesData<HabitLog[]>({ queryKey: keys.habits.allLogs() });
      queryClient.setQueriesData<HabitLog[]>({ queryKey: keys.habits.allLogs() }, (old) => {
        if (!old) return old;
        const rest = old.filter((l) => !(l.habit_id === v.habitId && l.date === v.date));
        return [...rest, { habit_id: v.habitId, date: v.date, done: v.done, updated_at: new Date().toISOString() }];
      });
      return { snap };
    },
    onError: (_e, _v, ctx) => ctx && ctx.snap.forEach(([k, d]) => queryClient.setQueryData(k, d)),
  });
}

export function useSaveHabit() {
  const qc = useQueryClient();
  return useMutation<Habit, ApiError, { id?: string; body: HabitCreate | HabitUpdate }>({
    mutationFn: (v) => (v.id ? api.habits.update(v.id, v.body as HabitUpdate) : api.habits.create(v.body as HabitCreate)),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.habits.list() }),
  });
}

export function useDeleteHabit() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.habits.remove(id),
    onMutate: (id) => {
      qc.setQueryData<Habit[]>(keys.habits.list(), (old) => (old ? old.filter((h) => h.id !== id) : old));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.habits.list() }),
  });
}
